// src/pages/league-dashboard/LeagueHome.js - COMPLETE WITH TEAM LINK DROPDOWNS
import React, { useState, useEffect } from 'react';
import { Users, Calendar, MessageSquare, Crown, AlertCircle, ArrowRight, Shield, Trophy } from 'lucide-react';
import { dynastyTheme } from '../../services/colorService';
import { leaguesAPI } from '../../services/apiService';
import MessageBoardPreview from '../../components/message-board/MessageBoardPreview';
import RotisserieTable from './standings/RotisserieTable';
import LeagueBanner from '../../components/league-dashboard/LeagueBanner';
import BoxScoreTile from '../../components/league-dashboard/BoxScoreTile';
import { fetchBoxScore, fetchAvailableDates } from '../../services/boxScoreApi';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';
import MLBPlayerNewsTile from '../../components/dashboard/tiles/MLBPlayerNewsTile';
import MLBTransactionsTile from '../../components/dashboard/tiles/MLBTransactionsTile';
import FreeAgentNewsTile from '../../components/dashboard/tiles/FreeAgentNewsTile';
import TradingBlockTile from '../../components/dashboard/tiles/TradingBlockTile';

const DEFAULT_TILE_ORDER = [
  { id: 'standings', label: 'Standings' },
  { id: 'message_board', label: 'Message Board' },
  { id: 'box_scores', label: 'Box Scores' },
  { id: 'mlb_player_news', label: 'MLB Player News' },
  { id: 'mlb_transactions', label: 'MLB Transactions' },
  { id: 'free_agent_news', label: 'Free Agent News' },
  { id: 'trading_block', label: 'Trading Block' },
  { id: 'private_messages', label: 'Private Messages' },
];

const SortableTile = ({ id, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    position: 'relative',
  };

  return (
    <div ref={setNodeRef} style={style}>
      <div
        {...attributes}
        {...listeners}
        className="absolute top-3 right-3 z-10 cursor-grab active:cursor-grabbing p-1 rounded opacity-30 hover:opacity-70 transition-opacity"
        title="Drag to reorder"
      >
        <GripVertical className="w-4 h-4 text-gray-400" />
      </div>
      {children}
    </div>
  );
};

const LeagueHome = ({ league, teams, setActiveSection, leagueId, userTeam, leagueBanner: leagueBannerProp, onSelectBoxScoreTeam, bannerConfig }) => {
  const [teamsWithLogos, setTeamsWithLogos] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [standingsData, setStandingsData] = useState(null);
  const [leagueBanner, setLeagueBanner] = useState(leagueBannerProp || null);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [settingsError, setSettingsError] = useState(null);
  const [scoringCategories, setScoringCategories] = useState({
    hitting: ['R', 'HR', 'RBI', 'SB', 'AVG', 'OPS'],
    pitching: ['W', 'SV', 'ERA', 'WHIP', 'SO', 'QS']
  });
  const [boxScores, setBoxScores] = useState({});
  const [boxScoresLoading, setBoxScoresLoading] = useState(false);
  const [yesterdayDate, setYesterdayDate] = useState(null);
  
  // Update banner if prop changes
  useEffect(() => {
    if (leagueBannerProp) {
      setLeagueBanner(leagueBannerProp);
    }
  }, [leagueBannerProp]);
  
  // Check if league needs setup
  const isInSetupMode = league?.league_status === 'setup' ||
                        league?.league_status === 'pricing' ||
                        league?.league_status === 'draft';

  // Tile ordering state
  const [tileOrder, setTileOrder] = useState(DEFAULT_TILE_ORDER);
  const [layoutLoaded, setLayoutLoaded] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Load saved layout on mount
  useEffect(() => {
    const loadLayout = async () => {
      try {
        const result = await leaguesAPI.getDashboardLayout(leagueId);
        if (result?.layout && Array.isArray(result.layout)) {
          const savedIds = result.layout.map(t => t.id);
          const newTiles = DEFAULT_TILE_ORDER.filter(t => !savedIds.includes(t.id));
          setTileOrder([...result.layout, ...newTiles]);
        }
      } catch (e) {
        // Silent fail — use default order
      } finally {
        setLayoutLoaded(true);
      }
    };
    if (leagueId) loadLayout();
  }, [leagueId]);

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = tileOrder.findIndex(t => t.id === active.id);
    const newIndex = tileOrder.findIndex(t => t.id === over.id);
    const newOrder = arrayMove(tileOrder, oldIndex, newIndex);
    setTileOrder(newOrder);
    try {
      await leaguesAPI.saveDashboardLayout(leagueId, newOrder);
    } catch (e) {
      // Silent fail — layout already updated in state
    }
  };

  // Fetch league settings including banner and categories
  useEffect(() => {
    if (leagueId) {
      fetchLeagueSettings();
    }
  }, [leagueId]);

  // Fetch team data including logos
  useEffect(() => {
    if (teams && teams.length > 0 && leagueId) {
      fetchTeamsWithLogos();
    }
    if (league?.league_settings?.scoring_system === 'rotisserie' || league?.league_settings?.scoring_system === 'roto' || league?.league_settings?.scoring_system === 'rotisserie_ytd') {
      fetchRotisserieStandings();
    }
  }, [teams, leagueId, league?.league_settings?.scoring_system, scoringCategories]);

  // Fetch box scores for all teams
  useEffect(() => {
    if (teamsWithLogos.length > 0 && leagueId) {
      fetchAllBoxScores();
    }
  }, [teamsWithLogos, leagueId]);

  const fetchLeagueSettings = async () => {
    if (!leagueId) return;
    
    setLoadingSettings(true);
    setSettingsError(null);
    
    try {
      console.log(`Fetching settings for league: ${leagueId}`);
      
      // Try the settings endpoint first
      const response = await leaguesAPI.getLeagueSettings(leagueId);
      
      if (response && response.success && response.settings) {
        // Set league banner
        if (response.settings.league_banner_url) {
          setLeagueBanner(response.settings.league_banner_url);
        }
        
        if (response.settings.scoring_categories) {
          const categories = response.settings.scoring_categories;
          
          setScoringCategories({
            hitting: categories.hitting || scoringCategories.hitting,
            pitching: categories.pitching || scoringCategories.pitching
          });
        }
      }
    } catch (error) {
      console.error('Could not fetch league settings:', error);
      
      // Try fallback to league details endpoint
      try {
        console.log('Trying fallback to league details endpoint...');
        const detailsResponse = await leaguesAPI.getLeagueDetails(leagueId);
        
        if (detailsResponse && detailsResponse.success && detailsResponse.league) {
          const leagueData = detailsResponse.league;
          
          // Try to get banner from league object
          if (leagueData.league_banner_url) {
            setLeagueBanner(leagueData.league_banner_url);
          }
          
          // Try to get categories from league object
          if (leagueData.scoring_categories) {
            const categories = leagueData.scoring_categories;
            
            setScoringCategories({
              hitting: categories.hitting || scoringCategories.hitting,
              pitching: categories.pitching || scoringCategories.pitching
            });
          }
        }
      } catch (fallbackError) {
        console.error('Fallback to league details also failed:', fallbackError);
        setSettingsError('Could not load league settings. Some features may be limited.');
        // Don't crash - continue with defaults
      }
    } finally {
      setLoadingSettings(false);
    }
  };

  const fetchTeamsWithLogos = async () => {
    if (!teams?.length || !leagueId) {
      setTeamsWithLogos([]);
      return;
    }
    
    try {
      setLoadingTeams(true);
      // USE STANDINGS API WHICH INCLUDES LOGOS!
      const response = await leaguesAPI.getLeagueStandings(leagueId);
      if (response.success && response.teams) {
        console.log('✅ Loaded teams with logos from standings:', response.teams.length);
        setTeamsWithLogos(response.teams);
        // Also update standingsData for the rest of the page
        setStandingsData(response);
      } else {
        setTeamsWithLogos(teams);
      }
    } catch (error) {
      console.log('Could not fetch team details:', error);
      setTeamsWithLogos(teams);
    } finally {
      setLoadingTeams(false);
    }
  };

  const fetchAllBoxScores = async () => {
    setBoxScoresLoading(true);
    
    try {
      // Get most recent game date from available dates (for replay functionality)
      // Use first team to determine available dates (all teams should have same game dates)
      const firstTeam = teamsWithLogos[0];
      if (!firstTeam) {
        console.warn('No teams available for box scores');
        setBoxScoresLoading(false);
        return;
      }

      console.log(`📅 Fetching available dates for team ${firstTeam.team_id}...`);
      const datesResponse = await fetchAvailableDates(leagueId, firstTeam.team_id);
      
      if (!datesResponse.success || !datesResponse.dates || datesResponse.dates.length === 0) {
        console.warn('No available game dates found');
        setBoxScoresLoading(false);
        return;
      }

      // Sort dates descending and get most recent
      const sortedDates = [...datesResponse.dates].sort((a, b) => b.localeCompare(a));
      const mostRecentDate = sortedDates[0];
      setYesterdayDate(mostRecentDate);
      
      console.log(`📊 Fetching box scores for ${teamsWithLogos.length} teams on ${mostRecentDate}`);
      
      // Fetch box scores in BATCHES to avoid overwhelming the backend
      const BATCH_SIZE = 3; // Process 3 teams at a time
      const boxScoresMap = {};
      
      for (let i = 0; i < teamsWithLogos.length; i += BATCH_SIZE) {
        const batch = teamsWithLogos.slice(i, i + BATCH_SIZE);
        
        const batchPromises = batch.map(team => 
          fetchBoxScore(leagueId, team.team_id, mostRecentDate)
            .then(data => ({ teamId: team.team_id, data }))
            .catch(err => ({ teamId: team.team_id, error: err }))
        );
        
        const batchResults = await Promise.all(batchPromises);
        
        // Add batch results to map
        batchResults.forEach(result => {
          if (result.error) {
            console.log(`⚠️ No box score for team ${result.teamId}:`, result.error.message);
            boxScoresMap[result.teamId] = { error: result.error };
          } else {
            boxScoresMap[result.teamId] = result.data;
          }
        });
        
        // Small delay between batches to avoid rate limiting
        if (i + BATCH_SIZE < teamsWithLogos.length) {
          await new Promise(resolve => setTimeout(resolve, 100)); // 100ms delay
        }
      }
      
      setBoxScores(boxScoresMap);
      console.log('✅ Box scores loaded:', Object.keys(boxScoresMap).length, 'teams');
    } catch (error) {
      console.error('❌ Error fetching box scores:', error);
    } finally {
      setBoxScoresLoading(false);
    }
  };

  const fetchRotisserieStandings = async () => {
    if (!leagueId) return;
    
    try {
      // Using REAL API CALL
      const response = await leaguesAPI.getLeagueStandings(leagueId);
      
      if (response.success && response.standings) {
        // Format the real standings data
        const formattedStandings = {
          categories: response.categories || scoringCategories,
          through_date: response.through_date || null,
          teams: response.standings.map(team => ({
            ...team,
            stats: team.stats || {},
            totalPoints: team.total_points || team.totalPoints || 0
          }))
        };
        
        setStandingsData(formattedStandings);
      } else {
        console.error('Failed to fetch standings:', response);
      }
    } catch (error) {
      console.error('Error fetching standings:', error);
      // Don't crash - standings will just not display
    }
  };

  const getSetupMessage = () => {
    switch(league?.league_status) {
      case 'setup':
        return 'Your league is in setup mode. Continue to Season Setup to configure prices and draft settings.';
      case 'pricing':
        return 'Player prices are being configured. Visit Season Setup to complete this process.';
      case 'draft':
        return 'League is in draft mode! Visit Season Setup to manage the draft process.';
      default:
        return null;
    }
  };

  const renderTile = (tileId) => {
    switch (tileId) {
      case 'standings':
        return (
          <div className={dynastyTheme.components.card.base}>
            <div className={`flex items-center justify-between p-4 border-b ${dynastyTheme.classes.border.neutral}`}>
              <div>
                <h3 className={`text-lg font-bold ${dynastyTheme.classes.text.white}`}>
                  {(league?.league_settings?.scoring_system === 'rotisserie' || league?.league_settings?.scoring_system === 'roto' || league?.league_settings?.scoring_system === 'rotisserie_ytd')
                    ? 'Rotisserie Standings'
                    : 'Current League Status'} - {teams.length}/{league?.league_settings?.max_teams || 12} Teams
                </h3>
                {standingsData?.through_date && (
                  <p className={`text-xs ${dynastyTheme.classes.text.neutralDark} mt-0.5`}>
                    Through {new Date(standingsData.through_date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </p>
                )}
              </div>
              <button
                onClick={() => setActiveSection('standings')}
                className={dynastyTheme.utils.getComponent('button', 'secondary', 'sm')}
              >
                View Full Standings
              </button>
            </div>
            {(league?.league_settings?.scoring_system === 'rotisserie' || league?.league_settings?.scoring_system === 'roto' || league?.league_settings?.scoring_system === 'rotisserie_ytd') ? (
              <div className="p-4">
                <RotisserieTable
                  data={standingsData}
                  userTeam={userTeam}
                  leagueId={leagueId}
                  shellless
                />
              </div>
            ) : (
              <div className="p-4">
                <div className="text-center py-8">
                  <p className={dynastyTheme.classes.text.neutralLight}>Head-to-head standings will appear here</p>
                </div>
              </div>
            )}
            {teams.length === 0 && (
              <div className="p-6 text-center">
                <Users className={`w-12 h-12 mx-auto mb-3 opacity-50 ${dynastyTheme.classes.text.neutralLight}`} />
                <h4 className={`${dynastyTheme.classes.text.white} font-semibold mb-2`}>League Setup in Progress</h4>
                <p className={`text-sm mb-4 ${dynastyTheme.classes.text.neutralLight}`}>
                  This league can support up to {league?.league_settings?.max_teams || 12} teams.
                  Once owners join, standings will appear here.
                </p>
                {league?.role === 'commissioner' && (
                  <button
                    onClick={() => setActiveSection('league-owners')}
                    className={dynastyTheme.utils.getComponent('button', 'primary', 'sm')}
                  >
                    Invite Team Owners
                  </button>
                )}
              </div>
            )}
          </div>
        );
      case 'message_board':
        return (
          <div className={dynastyTheme.components.card.base}>
            <div className={`p-4 border-b ${dynastyTheme.classes.border.neutral}`}>
              <h3 className={`text-lg font-bold ${dynastyTheme.classes.text.white}`}>League Message Board</h3>
            </div>
            <MessageBoardPreview
              leagueId={leagueId}
              onViewAll={() => setActiveSection('messages')}
            />
          </div>
        );
      case 'box_scores':
        return (
          <div className={dynastyTheme.components.card.base}>
            <div className={`p-4 border-b ${dynastyTheme.classes.border.neutral}`}>
              <h3 className={`text-lg font-bold ${dynastyTheme.classes.text.white}`}>
                Yesterday's Box Scores
              </h3>
              {yesterdayDate && (
                <p className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>
                  {yesterdayDate}
                </p>
              )}
            </div>
            <div className="overflow-x-auto" style={{ cursor: 'default' }}>
              <div
                className="flex gap-4 p-4"
                onMouseDown={(e) => {
                  if (e.target.classList.contains('flex') || e.target.classList.contains('p-4')) {
                    const ele = e.currentTarget.parentElement;
                    ele.style.cursor = 'grab';
                    const startX = e.pageX - ele.offsetLeft;
                    const scrollLeft = ele.scrollLeft;
                    const handleMouseMove = (e) => {
                      e.preventDefault();
                      const x = e.pageX - ele.offsetLeft;
                      const walk = (x - startX) * 2;
                      ele.scrollLeft = scrollLeft - walk;
                    };
                    const handleMouseUp = () => {
                      document.removeEventListener('mousemove', handleMouseMove);
                      document.removeEventListener('mouseup', handleMouseUp);
                      ele.style.cursor = 'default';
                    };
                    ele.style.cursor = 'grabbing';
                    document.addEventListener('mousemove', handleMouseMove);
                    document.addEventListener('mouseup', handleMouseUp);
                  }
                }}
              >
                {boxScoresLoading ? (
                  Array(3).fill(0).map((_, i) => (
                    <div key={i} className="flex-shrink-0 w-80 h-96 bg-neutral-800/50 rounded animate-pulse" />
                  ))
                ) : teamsWithLogos.length === 0 ? (
                  <div className={`text-center py-8 ${dynastyTheme.classes.text.neutralLight} w-full`}>
                    <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No teams in league yet</p>
                  </div>
                ) : (
                  (() => {
                    const standingsMap = {};
                    if (standingsData?.teams) {
                      standingsData.teams.forEach((team) => {
                        standingsMap[team.team_id] = team.totalPoints || team.total_points || 0;
                      });
                    }
                    const sortedTeams = [...teamsWithLogos].sort((a, b) => {
                      const aPoints = standingsMap[a.team_id] || 0;
                      const bPoints = standingsMap[b.team_id] || 0;
                      return bPoints - aPoints;
                    });
                    return sortedTeams.map(team => (
                      <BoxScoreTile
                        key={team.team_id}
                        teamId={team.team_id}
                        teamName={team.team_name}
                        teamLogoUrl={team.team_logo_url}
                        boxScoreData={boxScores[team.team_id]}
                        loading={boxScoresLoading}
                        error={boxScores[team.team_id]?.error}
                        date={yesterdayDate}
                        onViewFull={(teamId) => {
                          if (onSelectBoxScoreTeam) onSelectBoxScoreTeam(teamId);
                          setActiveSection('daily-box-scores');
                        }}
                      />
                    ));
                  })()
                )}
              </div>
            </div>
          </div>
        );
      case 'mlb_player_news':
        return <MLBPlayerNewsTile leagueId={leagueId} />;
      case 'mlb_transactions':
        return <MLBTransactionsTile />;
      case 'free_agent_news':
        return <FreeAgentNewsTile leagueId={leagueId} league={league} />;
      case 'trading_block':
        return (
          <TradingBlockTile
            leagueId={leagueId}
            league={league}
            currentUserTeamId={userTeam?.team_id || null}
            teams={teams}
          />
        );
      case 'private_messages':
        return (
          <div className={dynastyTheme.components.card.base}>
            <div className={`p-4 border-b ${dynastyTheme.classes.border.neutral}`}>
              <h3 className={`text-lg font-bold ${dynastyTheme.classes.text.white}`}>Private Messages</h3>
            </div>
            <div className="p-4 text-center">
              <MessageSquare className={`w-12 h-12 mx-auto mb-3 opacity-50 ${dynastyTheme.classes.text.neutralLight}`} />
              <p className={`text-sm mb-3 ${dynastyTheme.classes.text.neutralLight}`}>
                You have 0 unread messages
              </p>
              <button className={dynastyTheme.utils.getComponent('button', 'secondary', 'sm')}>
                View messages
              </button>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {settingsError && (
        <div className={`p-3 rounded-lg bg-yellow-500/20 text-yellow-400 flex items-center gap-2`}>
          <AlertCircle className="w-5 h-5" />
          <span className="text-sm">{settingsError}</span>
        </div>
      )}

      {isInSetupMode && league?.role === 'commissioner' && (
        <div className={`p-4 rounded-lg flex items-center gap-3 ${dynastyTheme.classes.bg.primaryLight} border ${dynastyTheme.classes.border.primaryBright} ${dynastyTheme.classes.text.primary}`}>
          <AlertCircle className="w-6 h-6" />
          <span className="flex-1 font-medium">{getSetupMessage()}</span>
          <button
            onClick={() => setActiveSection('season-setup')}
            className={`${dynastyTheme.utils.getComponent('button', 'primary', 'sm')} flex items-center gap-2`}
          >
            Go to Season Setup
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {isInSetupMode && league?.role !== 'commissioner' && (
        <div className={`p-4 rounded-lg bg-yellow-500/20 text-yellow-400 flex items-center gap-3`}>
          <AlertCircle className="w-6 h-6" />
          <span>The commissioner is currently setting up the league. You'll be notified when it's ready for action.</span>
        </div>
      )}

      <LeagueBanner
        leagueBanner={leagueBanner}
        title={league?.league_name || 'Loading...'}
        subtitle={`${league?.league_status?.replace(/_/g, ' ').toUpperCase()} • ${league?.current_season} Season • ${league?.league_settings?.scoring_system?.replace(/_/g, ' ')}`}
        badge={
          <>
            <span className={`${dynastyTheme.components.badge.success} ${leagueBanner ? 'backdrop-blur-md bg-neutral-900/60' : ''}`}>
              {league?.status?.toUpperCase() || 'LOADING'}
            </span>
            {league?.role === 'commissioner' && (
              <span className={`px-3 py-1 rounded text-sm font-semibold ${dynastyTheme.classes.bg.primary} ${dynastyTheme.classes.text.black} ${leagueBanner ? 'shadow-xl' : ''}`}>
                COMMISSIONER
              </span>
            )}
          </>
        }
        minHeight="160px"
        bannerConfig={bannerConfig}
      />

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={tileOrder.map(t => t.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-6">
            {tileOrder.map(tile => (
              <SortableTile key={tile.id} id={tile.id}>
                {renderTile(tile.id)}
              </SortableTile>
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
};

export default LeagueHome;